import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { loadFieldGroupsForMap } from '../utilities/getFields';
import {
  approximateIpLocation,
  browserLocation,
  coordinateForField,
  distanceInMeters,
  formatDistanceMiles,
  geolocationFailureMessage,
} from '../utilities/geo';
import { reportCaughtError } from '../utilities/errorTracking';
import '../css/FieldsMap.css';

const tileUrl = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const tileAttribution =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const defaultCenter = [39.5, -95.0];
const defaultZoom = 4;

const diamondPin = L.divIcon({
  className: 'ff-map-pin-wrap',
  html: '<div class="ff-map-pin"><div class="ff-map-pin-diamond"></div></div>',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));

// Interactive map of every field, mirroring the app's home map: diamond
// pins, locate-me, and a "nearest fields" list once we know where you are.
export const FieldsMap = () => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [nearestGroups, setNearestGroups] = useState([]);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState();
  const [isApproximateLocation, setIsApproximateLocation] = useState(false);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) {
      return undefined;
    }

    const map = L.map(containerRef.current, {
      center: defaultCenter,
      zoom: defaultZoom,
      scrollWheelZoom: false,
    });
    L.tileLayer(tileUrl, { attribution: tileAttribution, maxZoom: 19 }).addTo(map);
    mapRef.current = map;

    loadFieldGroupsForMap()
      .then(fieldGroups => {
        setGroups(fieldGroups);
        fieldGroups.forEach(group => {
          const coordinate = coordinateForField(group.data);
          const marker = L.marker([coordinate.latitude, coordinate.longitude], {
            icon: diamondPin,
            title: group.data.group_name,
          }).addTo(map);
          marker.bindPopup(
            `<a class="ff-map-popup-link" href="/fields/${encodeURIComponent(group.id)}">${escapeHtml(group.data.group_name)}</a>`,
          );
        });

        if (fieldGroups.length > 0) {
          const bounds = L.latLngBounds(
            fieldGroups.map(group => {
              const coordinate = coordinateForField(group.data);
              return [coordinate.latitude, coordinate.longitude];
            }),
          );
          map.fitBounds(bounds, { padding: [30, 30], maxZoom: 10 });
        }
      })
      .catch(error => reportCaughtError(error, 'fields-map-load'));

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  const handleNearMe = async () => {
    setIsLocating(true);
    setLocationError(undefined);
    setIsApproximateLocation(false);

    let location;
    try {
      location = await browserLocation();
    } catch (geolocationError) {
      // Precise geolocation failed (commonly: OS Location Services is off
      // for the browser even though the site permission was granted). Fall
      // back to city-level IP location rather than a dead end.
      reportCaughtError(geolocationError, 'near-me-geolocation');
      try {
        location = await approximateIpLocation();
        setIsApproximateLocation(true);
      } catch (ipError) {
        reportCaughtError(ipError, 'near-me-ip-fallback');
        setLocationError(geolocationFailureMessage(geolocationError));
        setIsLocating(false);
        return;
      }
    }

    try {
      const map = mapRef.current;
      if (map) {
        map.setView([location.latitude, location.longitude], 10);
        L.circleMarker([location.latitude, location.longitude], {
          radius: 7,
          color: '#FFB703',
          fillColor: '#0B6E4F',
          fillOpacity: 1,
          weight: 3,
        }).addTo(map);
      }

      const nearest = [...groups]
        .map(group => {
          const coordinate = coordinateForField(group.data);
          return { group, distance: distanceInMeters(location, coordinate) };
        })
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 5);
      setNearestGroups(nearest);
    } catch (error) {
      reportCaughtError(error, 'near-me-results');
      setLocationError('Something went wrong showing nearby fields. Please try again.');
    } finally {
      setIsLocating(false);
    }
  };

  return (
    <div className="ff-fields-map-block">
      <div className="ff-fields-map-toolbar">
        <h2 className="ff-fields-map-title">Find a field</h2>
        <button
          className="ff-button ff-button-primary"
          disabled={isLocating}
          onClick={handleNearMe}
          type="button"
        >
          {isLocating ? 'Locating…' : 'Fields near me'}
        </button>
      </div>
      {locationError ? <p className="ff-fields-map-error">{locationError}</p> : null}
      <div className="ff-fields-map ff-card" ref={containerRef} />
      {nearestGroups.length > 0 ? (
        <div className="ff-nearest-list">
          <p className="ff-kicker">Closest to you</p>
          {isApproximateLocation ? (
            <p className="ff-fields-map-approximate">
              Based on your approximate (network) location — enable Location
              Services for your browser for precise results.
            </p>
          ) : null}
          {nearestGroups.map(({ group, distance }) => (
            <button
              className="ff-nearest-row"
              key={group.id}
              onClick={() => navigate(`/fields/${group.id}`)}
              type="button"
            >
              <span className="ff-nearest-name">{group.data.group_name}</span>
              <span className="ff-nearest-distance">{formatDistanceMiles(distance)}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
